import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { Skeleton } from '@/components/feedback';
import { EntityTable, type EntityTableColumn, PillButton } from '@/components/entity';
import { TIME_SLOTS } from '@/routes/publicProperties/components/UnitEngagementDialog';
import { notifyShowingStatusUpdated } from '@/lib/services/emailNotifications';
import { listShowings, updateShowingStatus, type ShowingWithLead } from '@/lib/services/showings';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import type { ShowingStatus } from '@/lib/types/showing';
import { formatUnitLabel, humanizeAddressText } from '@/lib/utils/propertyFormatting';
import { formatLeadDisplayName } from '@/lib/utils/nameFormatting';

const SHOWING_LABELS: Record<ShowingStatus, string> = {
  requested: 'Requested',
  confirmed: 'Confirmed',
  rescheduled: 'Rescheduled',
  cancelled: 'Cancelled',
};

const EMPTY_VALUE = '—';

interface RescheduleDraft {
  date: string;
  time: string;
}

interface ShowingRequestRow {
  id: string;
  leadId: string;
  visitorName: string;
  visitorEmail: string;
  visitorPhone: string;
  preferredDate: string;
  preferredTime: string;
  message: string | null;
  status: ShowingStatus;
  createdAt: string;
  unitLabel: string;
}

export function ShowingRequestsPanel(): JSX.Element {
  const user = useDashboardUser();
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Partial<Record<string, RescheduleDraft>>>({});

  const showingsQuery = useQuery<ShowingWithLead[]>({
    queryKey: ['dashboard-showings', { status: 'requested' }],
    queryFn: async ({ signal }) => {
      return await listShowings({ user, filters: { status: 'requested' }, signal });
    },
  });

  const visibleShowings = useMemo(() => {
    const items: ShowingRequestRow[] = (showingsQuery.data ?? []).map((entry) => {
      const showing = entry.showing;
      const lead = entry.lead;
      const preferredDateSource = showing.scheduled_at ?? showing.preferred_date ?? '';
      const preferredTimeSource = showing.scheduled_at ?? showing.preferred_time ?? '';
      const preferredDate =
        typeof preferredDateSource === 'string' && preferredDateSource.length >= 10
          ? preferredDateSource.slice(0, 10)
          : '';
      const preferredTime =
        typeof preferredTimeSource === 'string' && preferredTimeSource.length >= 16
          ? preferredTimeSource.slice(11, 16)
          : '';
      const unit = entry.unit;
      const unitDisplay = unit ? humanizeAddressText(formatUnitLabel(unit)) : '';
      const propertyCode = unit ? humanizeAddressText(unit.propertyCode) : '';
      const hasUnitMeta = unitDisplay.trim().length > 0 && propertyCode.trim().length > 0;
      const unitLabel = hasUnitMeta
        ? `${unitDisplay} · ${propertyCode}`
        : (showing.unit_id ?? 'Unit not set');
      const visitorName = formatLeadDisplayName({
        firstName: lead.first_name,
        lastName: lead.last_name,
        fullName: lead.full_name,
      });
      const visitorEmail = lead.email;
      return {
        id: showing.id,
        leadId: lead.id,
        visitorName,
        visitorEmail,
        visitorPhone: lead.phone ?? '',
        preferredDate,
        preferredTime,
        message: showing.message ?? null,
        status: showing.status as ShowingStatus,
        createdAt: showing.created_at,
        unitLabel,
      };
    });

    return items.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [showingsQuery.data]);

  const updateDraft = (
    id: string,
    patch: Partial<RescheduleDraft>,
    defaults: RescheduleDraft,
  ): void => {
    setDrafts((current) => {
      const existingDraft = current[id];
      const nextDate =
        typeof patch.date === 'string'
          ? patch.date
          : existingDraft !== undefined
            ? existingDraft.date
            : defaults.date;
      const nextTime =
        typeof patch.time === 'string'
          ? patch.time
          : existingDraft !== undefined
            ? existingDraft.time
            : defaults.time;

      return {
        ...current,
        [id]: { date: nextDate, time: nextTime },
      };
    });
  };

  const invalidateShowings = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['dashboard-showings'] });
  };

  const statusMutation = useMutation({
    mutationFn: async (args: { id: string; status: ShowingStatus; scheduledAt?: string | null }) =>
      await updateShowingStatus({
        user,
        showingId: args.id,
        status: args.status,
        scheduledAt: args.scheduledAt ?? null,
      }),
    onSuccess: async (result) => {
      void notifyShowingStatusUpdated({
        showingId: result.showing.id,
        leadEmail: result.lead.email,
        newStatus: result.showing.status,
        scheduledAt: result.showing.scheduled_at,
        unitId: result.showing.unit_id,
      });
      await invalidateShowings();
    },
  });

  const handleApprove = (id: string): void => {
    statusMutation.mutate({ id, status: 'confirmed' });
  };

  const handleDeny = (id: string): void => {
    statusMutation.mutate({ id, status: 'cancelled' });
  };

  const handleReschedule = (id: string): void => {
    const showing = visibleShowings.find((entry) => entry.id === id);
    if (!showing) return;

    const draft = drafts[id];
    const draftDate = draft !== undefined ? draft.date.trim() : '';
    const draftTime = draft !== undefined ? draft.time.trim() : '';
    const nextDate = draftDate.length > 0 ? draftDate : showing.preferredDate;
    const nextTime = draftTime.length > 0 ? draftTime : showing.preferredTime;

    if (nextDate.trim().length === 0 || nextTime.trim().length === 0) {
      return;
    }

    const scheduledAt = new Date(`${nextDate}T${nextTime}:00`).toISOString();

    statusMutation.mutate({ id, status: 'rescheduled', scheduledAt });
  };

  const busy = statusMutation.isPending;

  const columns: ReadonlyArray<EntityTableColumn<ShowingRequestRow>> = [
    {
      id: 'lead',
      header: 'Lead',
      renderCell: (showing) => (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/dashboard/leads/${showing.leadId}`}
              className="font-semibold text-text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/30"
            >
              {showing.visitorName}
            </Link>
            <StatusBadge label={SHOWING_LABELS[showing.status]} tone="info" />
          </div>
          <span className="text-xs text-text-primary/70">
            {showing.visitorEmail} · {showing.visitorPhone || 'No phone'}
          </span>
        </div>
      ),
    },
    {
      id: 'unit',
      header: 'Unit',
      renderCell: (showing) => (
        <span className="whitespace-nowrap font-semibold text-text-primary">
          {showing.unitLabel}
        </span>
      ),
    },
    {
      id: 'slot',
      header: 'Preferred slot',
      renderCell: (showing) => (
        <span className="whitespace-nowrap font-semibold text-text-primary">
          {formatSlot(showing.preferredDate, showing.preferredTime)}
        </span>
      ),
    },
    {
      id: 'message',
      header: 'Message',
      renderCell: (showing) => {
        const message = normalizeMessage(showing.message);
        if (message === null) {
          return <span className="text-sm text-text-primary/50">{EMPTY_VALUE}</span>;
        }

        return (
          <span
            className="block max-w-[20rem] truncate text-sm text-text-primary/80"
            title={message}
          >
            {message}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      renderCell: (showing) => {
        const draft = drafts[showing.id];
        const draftDate = draft !== undefined ? draft.date : showing.preferredDate;
        const draftTime = draft !== undefined ? draft.time : showing.preferredTime;

        const showDraftTimeOption = TIME_SLOTS.some((slot) => slot.value === draftTime);

        return (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <PillButton
              size="sm"
              variant="secondary"
              disabled={busy}
              className="border-emerald-300/60 bg-emerald-50 text-emerald-800 hover:border-emerald-300 hover:bg-emerald-100"
              onClick={() => {
                handleApprove(showing.id);
              }}
            >
              Approve
            </PillButton>
            <PillButton
              size="sm"
              variant="secondary"
              disabled={busy}
              className="border-rose-300/60 bg-rose-50 text-rose-800 hover:border-rose-300 hover:bg-rose-100"
              onClick={() => {
                handleDeny(showing.id);
              }}
            >
              Deny
            </PillButton>
            <input
              type="date"
              value={draftDate}
              disabled={busy}
              aria-label="Reschedule date"
              title="Reschedule date"
              onChange={(event) => {
                updateDraft(
                  showing.id,
                  { date: event.target.value },
                  { date: showing.preferredDate, time: showing.preferredTime },
                );
              }}
              className="h-9 rounded-lg border border-surface-subtle bg-white px-3 text-sm shadow-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 disabled:cursor-not-allowed disabled:opacity-70"
            />
            <select
              value={draftTime}
              disabled={busy}
              aria-label="Reschedule time"
              title="Reschedule time"
              onChange={(event) => {
                updateDraft(
                  showing.id,
                  { time: event.target.value },
                  { date: showing.preferredDate, time: showing.preferredTime },
                );
              }}
              className="h-9 rounded-lg border border-surface-subtle bg-white px-3 text-sm shadow-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {showDraftTimeOption ? null : <option value={draftTime}>{draftTime}</option>}
              {TIME_SLOTS.map((slot) => (
                <option key={slot.value} value={slot.value}>
                  {slot.label}
                </option>
              ))}
            </select>
            <PillButton
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => {
                handleReschedule(showing.id);
              }}
            >
              Reschedule
            </PillButton>
          </div>
        );
      },
    },
  ];

  if (showingsQuery.isLoading) {
    return <ShowingRequestsSkeleton />;
  }

  if (showingsQuery.isError) {
    return (
      <section className="rounded-2xl border border-rose-200 bg-white/60 p-5">
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-rose-600">Unable to load showing requests.</p>
          <p className="text-xs text-text-primary/60">Refresh the page and try again.</p>
        </div>
      </section>
    );
  }

  if (visibleShowings.length === 0) {
    return (
      <section className="rounded-2xl border border-dashed border-surface-subtle bg-white/60 p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold text-brand-primary">Showing requests</p>
            <p className="text-xs text-text-primary/60">No pending requests right now.</p>
          </div>
          <StatusBadge label="All caught up" tone="neutral" />
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <p className="text-sm font-semibold uppercase tracking-wide text-brand-primary/70">
            Showing requests
          </p>
          <p className="text-base font-semibold text-text-primary">
            {visibleShowings.length} pending
          </p>
        </div>
      </div>
      <p className="text-xs text-text-primary/60">All times shown in America/Toronto.</p>

      <div className="overflow-hidden rounded-2xl border border-surface-subtle bg-white shadow-sm">
        <EntityTable
          items={visibleShowings}
          columns={columns}
          getRowKey={(showing) => showing.id}
          emptyState={
            <div className="p-6 text-center text-sm text-text-primary/70">No pending requests.</div>
          }
        />
      </div>
    </section>
  );
}

function ShowingRequestsSkeleton(): JSX.Element {
  return (
    <section className="flex flex-col gap-4" aria-label="Loading showing requests">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-6 w-24" />
        </div>
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>
      <div className="overflow-hidden rounded-2xl border border-surface-subtle bg-white shadow-sm">
        <div className="divide-surface-muted divide-y">
          {Array.from({ length: 4 }).map((_row, index) => (
            <Skeleton key={`showing-row-${String(index)}`} className="h-14 w-full rounded-none" />
          ))}
        </div>
      </div>
    </section>
  );
}

function formatSlot(date: string, time: string): string {
  const dateLabel = formatDateLabel(date);
  const match = TIME_SLOTS.find((slot) => slot.value === time);
  const timeLabel = match ? match.label : time;
  return `${dateLabel} at ${timeLabel}`;
}

function formatDateLabel(value: string): string {
  if (!value) return 'Date not set';
  return new Date(value).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function normalizeMessage(value: string | null): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.replace(/\s+/g, ' ').trim();
  return normalized.length > 0 ? normalized : null;
}
